import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, MARKS } from '@contentful/rich-text-types';
import { Box, Button, Typography } from '@heycater/design-system';
import { StaticImageData } from 'next/image';
import Link from 'next/link';
import styled from 'styled-components';

import defaultImage from '@images/hub/banner/image-layer.jpg';
import HubBannerImage from 'hub/components/HubBanner/HubBannerImage';

type Props = {
  title: Document;
  subtitle?: string;
  image?: {
    src: string | StaticImageData;
    alt: string;
  };
  button: {
    text: string;
    url: string;
  };
};

const HubBanner = (props: Props) => {
  const parsedTitle = documentToReactComponents(props.title, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <span>{children}</span>,
    },
    renderMark: {
      [MARKS.BOLD]: (text) => <span style={{ color: '#006C4C' }}>{text}</span>,
    },
  });
  return (
    <Wrapper as="aside">
      <ContentWrapper>
        <Box>
          <Typography
            variant="h4"
            fontSize={{ xs: 24, sm: 28, md: 32 }}
            fontWeight={700}
            lineHeight="130%"
          >
            {parsedTitle}
          </Typography>
          {props.subtitle && (
            <Typography
              mt={1}
              component="p"
              lineHeight="160%"
              fontWeight="500"
              fontSize={{ xs: 14, sm: 16 }}
            >
              {props.subtitle}
            </Typography>
          )}
        </Box>
        <Box mt={4}>
          <Link href={props.button.url} passHref>
            <HubBannerButton variant="primary" rounded component={'a'}>
              <Typography fontSize="16px" fontWeight="600" lineHeight="172.5%">
                {props.button.text}
              </Typography>
            </HubBannerButton>
          </Link>
        </Box>
      </ContentWrapper>

      <ImageWrapper>
        <HubBannerImage
          src={props.image?.src || defaultImage}
          alt={props.image?.alt || ''}
        />
      </ImageWrapper>
    </Wrapper>
  );
};

const HubBannerButton = styled(Button)`
  height: 40px;
  padding: 6px 15px;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column-reverse;
  border-radius: ${({ theme }) => theme.spacing(1)}px;
  background: var(
    --radial-gradient,
    radial-gradient(
      92.76% 128.27% at 93.04% 116.94%,
      #e2ec97 7.76%,
      #e7f4c3 79.45%
    )
  );

  text-align: center;

  margin-top: ${({ theme }) => theme.spacing(8)}px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-top: 0px;
    flex-direction: row;
    text-align: start;
  }
`;

const ImageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing(0, 3, 0, 3)};
  margin-right: ${({ theme }) => theme.spacing(2)}px;
  margin-top: ${({ theme }) => theme.spacing(-6)}px;
  flex-shrink: 0;

  max-width: 320px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    padding: 0;
    width: min(40vw, 280px);
    margin: ${({ theme }) => theme.spacing(2, -1, 2, 0)};
    max-height: calc(100% - 60px);
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    max-width: 320px;
    width: auto;
    margin: ${({ theme }) => theme.spacing(3, -3, 3, 0)};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-self: stretch;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(2, 2, 3, 2)};
  align-items: center;
  height: auto;
  justify-content: space-around;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    align-items: start;
    padding: ${({ theme }) => theme.spacing(4, 2, 4, 4)};
  }
`;
export default HubBanner;
